export const getTodayString = () => {
  const now = new Date();
  if (now.getHours() < 3) {
    now.setDate(now.getDate() - 1);
  }
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const year = now.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatPlayerName = (fullName, isMobile, isStarter = false, sport = null) => {
  if (!fullName) return '';
  let formattedName = fullName;
  
  if (isMobile) {
    const nameParts = fullName.split(' ');
    if (nameParts.length >= 2) {
      const firstName = nameParts[0];
      if (nameParts.length >= 3) {
        // For 3+ words, show first initial + last two words (e.g., "Michael Harris II" -> "M. Harris II")
        const lastTwoWords = nameParts.slice(-2).join(' ');
        formattedName = `${firstName[0]}. ${lastTwoWords}`;
      } else {
        // For 2 words, show first initial + last word (e.g., "John Smith" -> "J. Smith")
        const lastName = nameParts[nameParts.length - 1];
        formattedName = `${firstName[0]}. ${lastName}`;
      }
    }
  }
  
  // For MLB and NFL, don't add asterisk 
  // MLB has batting order numbers, NFL doesn't need starter indicators
  if (sport === 'mlb' || sport === 'nfl') {
    return formattedName;
  }
  
  // For other sports (WNBA, etc.), keep the asterisk for starters
  // removed asterix
  return isStarter ? `${formattedName}` : formattedName;
};

export const getTeamLogoUrl = (team, isDarkMode = false, sport = 'mlb') => {
  // Team abbreviation mapping if needed
  // The API should ideally return team abbreviations like 'MIN', 'NYY', etc.
  // If it returns team IDs, we may need a mapping here
  if (!team) return '';

  // Handle case where team is an object (e.g., team data object) instead of string
  if (typeof team !== 'string') {
    // Try to extract abbreviation from object
    if (team.abbreviation) {
      team = team.abbreviation;
    } else if (team.abbrev) {
      team = team.abbrev;
    } else if (team.teamAbbr) {
      team = team.teamAbbr;
    } else {
      return '';
    }
  }

  // Per-sport abbreviation corrections: feed-side abbrev → ESPN logo-file
  // abbrev. Add to the inner dict whenever a logo 404s because the feed uses
  // a different code than ESPN's URL.
  const LOGO_ABBREV_MAP = {
    nfl: {
      'WAS': 'WSH',  // Washington Commanders
      'JAC': 'JAX',  // Jacksonville Jaguars
    },
    nhl: {
      'WAS': 'WSH',  // Washington Capitals
      'TBL': 'TB',   // Tampa Bay Lightning
      'SJS': 'SJ',   // San Jose Sharks
      'NJD': 'NJ',   // New Jersey Devils
      'LAK': 'LA',   // Los Angeles Kings
      'WIN': 'WPG',  // Winnipeg Jets
    },
    nba: {
      'WAS': 'WSH',  // Washington Wizards
      'PHO': 'PHX',  // Phoenix Suns
      'GSW': 'GS',   // Golden State Warriors
      'CHO': 'CHA',  // Charlotte Hornets
      'BRK': 'BKN',  // Brooklyn Nets
      'NOP': 'NO',   // New Orleans Pelicans
      'SAS': 'SA',   // San Antonio Spurs
    },
    wnba: {
      // WNBA mismatches go here as they surface.
    },
    mlb: {
      'WAS': 'WSH',  // Washington Nationals
      'WSN': 'WSH',  // Washington Nationals (alt)
      'CWS': 'CHW',  // Chicago White Sox
      'SFG': 'SF',   // San Francisco Giants
      'TBR': 'TB',   // Tampa Bay Rays
      'KCR': 'KC',   // Kansas City Royals
    },
  };
  const sportMap = LOGO_ABBREV_MAP[sport] || {};
  let teamAbbr = sportMap[team.toUpperCase()] || team;
  
  // Convert to lowercase for URL
  teamAbbr = teamAbbr.toLowerCase();

  // Sport-specific logo paths. ESPN hosts both /500/ (light-background
  // logos, meant to sit on white) and /500-dark/ (versions optimized for
  // dark UI). Honor isDarkMode across every sport that has a dark variant.
  let logoPath;
  if (sport === 'nfl') {
    // NFL "scoreboard" variants are the ones with dark backgrounds; the
    // base /500/ pngs here already read fine on dark UIs.
    logoPath = isDarkMode ? 'nfl/500-dark/scoreboard' : 'nfl/500/scoreboard';
  } else if (sport === 'mlb') {
    logoPath = isDarkMode ? 'mlb/500-dark' : 'mlb/500';
  } else if (sport === 'nba') {
    logoPath = isDarkMode ? 'nba/500-dark' : 'nba/500';
  } else if (sport === 'wnba') {
    logoPath = 'wnba/500-dark';
  } else if (sport === 'nhl') {
    logoPath = isDarkMode ? 'nhl/500-dark' : 'nhl/500';
  } else {
    // Default fallback
    logoPath = isDarkMode ? `${sport}/500-dark` : `${sport}/500`;
  }

  return `https://a.espncdn.com/i/teamlogos/${logoPath}/${teamAbbr}.png`;
};

export const getBookLogoUrl = (book) => `/logos/${book.toLowerCase()}.png`;

export const getPlayerHeadshotUrl = (playerId, sport) => {
  if (!playerId) return null;
  const id = String(playerId);
  switch (sport) {
    case 'mlb': {
      const numericId = id.replace('mlb_', '');
      return `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:silo:current.png/r_max/w_180,q_auto:best/v1/people/${numericId}/headshot/silo/current`;
    }
    case 'nba':
    case 'wnba': {
      const numericId = id.replace('nba_', '').replace('wnba_', '');
      return `https://a.espncdn.com/combiner/i?img=/i/headshots/nba/players/full/${numericId}.png&w=96&h=70&cb=1`;
    }
    case 'nfl': {
      const numericId = id.replace('nfl_', '');
      return `https://a.espncdn.com/i/headshots/nfl/players/full/${numericId}.png`;
    }
    case 'nhl': {
      const numericId = id.replace('nhl_', '');
      return `https://assets.nhle.com/mugs/nhl/latest/${numericId}.png`;
    }
    case 'soccer': {
      const numericId = id.replace('soccer_', '');
      return `https://media.api-sports.io/football/players/${numericId}.png`;
    }
    default:
      return null;
  }
};

export const formatGameTime = (timeStr) => {
  if (!timeStr || timeStr === 'TBD' || timeStr === 'TBA') return timeStr;

  // Check if it's a full datetime string like "2025-10-08 07:00:00 PM EDT"
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    const parts = timeStr.split(' ');
    // Format is: [date, time, period, timezone]
    // e.g., ["2025-10-08", "07:00:00", "PM", "EDT"]
    if (parts.length >= 3) {
      const time = parts[1]; // "07:00:00"
      const period = parts[2]; // "PM"
      // Remove seconds: "07:00:00" -> "07:00"
      const timePart = time.split(':').slice(0, 2).join(':');
      return `${timePart} ${period}`;
    }
    // Already in simple format like "8:00 PM"
    return timeStr;
  }

  // Parse the 24-hour time string (e.g., "19:10")
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr; // Return as-is if not in expected format

  const hour = parseInt(parts[0]);
  const minute = parseInt(parts[1]);

  if (isNaN(hour) || isNaN(minute)) return timeStr; // Return as-is if parsing failed

  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
};

// Prefix the rendered time with day-of-week context for future games so
// users can tell upcoming games apart at a glance:
//   - Today                → '' (just the time)
//   - Tomorrow – 7 days    → 'Thursday '
//   - More than 7 days out → 'Thursday 6/11 '
export const formatGameDayPrefix = (gameDateTime, now = new Date()) => {
  if (!gameDateTime) return '';

  // Compare calendar days (not raw ms) so "today" is stable regardless of
  // when in the day the game starts.
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfGame = new Date(gameDateTime);
  startOfGame.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((startOfGame - startOfToday) / (1000 * 60 * 60 * 24));

  if (dayDiff <= 0) return '';

  const dayName = gameDateTime.toLocaleDateString('en-US', { weekday: 'long' });
  if (dayDiff <= 7) return `${dayName} `;
  const m = gameDateTime.getMonth() + 1;
  const d = gameDateTime.getDate();
  return `${dayName} ${m}/${d} `;
};